/**
 * Agents first (train 1b): the converse hook's agent half, in a leaf of its own
 * the way the task-screen door's chain step is (R-L9). Everything about WHO an
 * ask goes to, who the thread frame says answered it and whose page the
 * exchange is filed on lives here; the hook keeps the SSE lane, the Line, the
 * door chain and the classic-lane fallbacks, and lends this leaf the one ref
 * the two halves share.
 *
 * Rule D, which the hook points at from three sites: the classic socket lane
 * stamps agentSlug 'bash', so an agent's ask may never ride it, and the Line
 * builds its turn with no handle, so an agent's typed words down that door
 * would reach Bash on Bash's thread and come back under the agent's name. An
 * ask that named a handle therefore rides the SSE lane even with a call open,
 * and when that lane gives nothing back it lands on the error card instead of
 * the classic lane: its Retry re-runs it on the agent THE ASK WAS MADE TO,
 * which is not always the one picked when the button is pressed. Only the lead
 * falls back to the classic task lane. Nothing is lost either way.
 */
import { useCallback, useRef, useState, type RefObject } from "react";
import type {
  FileAttachment,
  ImageAttachment,
  RouterEvent,
} from "@bash/shared";
import {
  useComposerHistoryStore,
  type ComposerHistoryEntry,
} from "@/stores/composerHistoryStore";
import { isAgentsV1 } from "@/lib/home-flags";
import { useTeamStore } from "@/stores/teamStore";

/** The thread frame's two person-facing fields, carried onto the hook's result. */
export interface ConverseAgentFrame {
  /** Agents first (train 1b): the agent the THREAD FRAME says answered this
   *  turn; null when the lead did (its own turn, or a named agent that could
   *  not). Reset at every submit. */
  answeredBy: { handle: string; name: string } | null;
  /** The server's one person-facing line when a named agent did not answer. */
  note: string | null;
}

/** The whole ask, so a retry can re-send what was actually asked. A photo-only
 *  send has no words, and a retry carrying only text sent nothing at all. */
export interface LastAsk {
  text: string;
  images?: ImageAttachment[];
  files?: FileAttachment[];
  /** Agents first (train 1b): the handle this ask was made TO, stamped at
   *  send time. A retry re-runs it on that agent, not on whoever is picked
   *  when the button is pressed: the words were written for the one the
   *  person was talking to. */
  agent?: string;
}

/** One run's agent frame: who it is for, what its thread frame said, and the row it files. */
export interface ConverseAgentTurn {
  /** The handle this run is FOR; `undefined` is the lead. */
  agent: string | undefined;
  /** The thread frame as it lands: who answered, and why the lead did. */
  readFrame: (ev: Extract<RouterEvent, { type: "thread" }>) => void;
  /** A settled exchange, filed on the page of whoever ANSWERED it, under
   *  the ask (`question`) and the request id (`id`) beginTurn was handed. */
  record: (e: Omit<ComposerHistoryEntry, "id" | "ts" | "question">) => void;
}

export interface ConverseAgentDesk {
  /** The two fields the pill reads, spread onto the hook's result. */
  frame: ConverseAgentFrame;
  /**
   * A new send begins. Neither frame field is cleared on dismiss: the folded
   * thread is held for its 320ms transition and the row reads both, so a field
   * dropped on the way out changes the row under the fold: a turn the lead
   * covered would stop saying Bash, and an agent's own turn would stop saying
   * the agent. Here, at the next submit, is where both reset, and neither can
   * outlive its thread anyway: the render guard is the held thread AND the
   * field, so once the fold ends there is nothing left to paint under a later
   * panel. The pick is read ONCE for the whole send and returned, so the ask
   * remembers who it was made to and the run is told the same handle: a retry
   * can never disagree with the request that failed.
   */
  beginSend: () => string | undefined;
  /** One run's frame. `threadIdRef` is the hook's, lent for the rule inside. */
  beginTurn: (
    forAgent: string | undefined,
    threadIdRef: RefObject<string | undefined>,
    question: string,
    id: string
  ) => ConverseAgentTurn;
}

/** The agent the next ask goes to, read at SEND time the way the model pick
 *  is, and only while the flag is on. `undefined` is the lead. */
const pickedAgent = (): string | undefined =>
  isAgentsV1()
    ? (useTeamStore.getState().selectedHandle ?? undefined)
    : undefined;

export function useConverseAgent(): ConverseAgentDesk {
  const [answeredBy, setAnsweredBy] = useState<{
    handle: string;
    name: string;
  } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** The pick `threadIdRef` was minted under. A thread id belongs to ONE
   *  conversation, so when this and the current pick disagree the ref is
   *  dropped before the next request is built (rule B). */
  const agentRef = useRef<string | undefined>(undefined);

  const beginSend = useCallback((): string | undefined => {
    setAnsweredBy(null);
    setNote(null);
    return pickedAgent();
  }, []);

  const beginTurn = useCallback(
    (
      /** The agent this run is FOR. A retry names the handle its ask was made
       *  to and it wins over the live pick, so switching the strip between a
       *  failure and Retry never hands one agent's words to another. Absent =
       *  whoever is picked right now. */
      forAgent: string | undefined,
      threadIdRef: RefObject<string | undefined>,
      /** The ask as the server stores it (`req.input`, trimmed): an untrimmed
       *  mirror row never matched the server's and painted the ask twice. */
      question: string,
      /** The request's own id (`req.idempotencyKey`), which the server stores
       *  as the row's id (one id per exchange, 2026-09-28): the mirror row
       *  and its server twin are then one row by id, and the history card
       *  never remounts it when the server page lands. */
      id: string
    ): ConverseAgentTurn => {
      // The pick is read at SEND time, the way the model pick is, and only
      // while the flag is on. The thread ref belongs to the agent it was
      // minted under, so a switched pick drops it here, before the request
      // names anything: it could only have named the previous conversation.
      // The server refuses a foreign id too (daemonArm resolves the thread
      // from (user, agent)), so this is the near half of a guard with two
      // halves.
      const agentsOn = isAgentsV1();
      const agent = forAgent ?? pickedAgent();
      if (agent !== agentRef.current) {
        agentRef.current = agent;
        threadIdRef.current = undefined;
      }
      // The mirror files the exchange under WHO ANSWERED, not who was asked.
      // When a named agent could not take the turn the lead answered and the
      // server filed it under the lead, so a row carrying the agent would put
      // its name over words it never wrote. A frame that carries the key is the
      // truth, and `agent: null` IS the lead, so that row carries no handle.
      let filedUnder = agent;
      return {
        agent,
        readFrame: (ev) => {
          // The frame says who answered, and why the lead did when a named
          // agent could not. Only an ask that named a handle carries these
          // keys, so the lead's own turn writes neither. The flag is read here
          // too: with agents off the client ignores the keys whatever a server
          // sends, so the lead's turn is byte-identical on BOTH sides.
          if (agentsOn && ev.agent !== undefined) filedUnder = ev.agent?.handle;
          if (agentsOn && ev.agent) setAnsweredBy(ev.agent);
          if (agentsOn && ev.note) setNote(ev.note);
        },
        record: (e) =>
          useComposerHistoryStore.getState().add({
            ...e,
            id,
            question,
            ...(filedUnder ? { agent: filedUnder } : {}),
          }),
      };
    },
    []
  );

  return { frame: { answeredBy, note }, beginSend, beginTurn };
}
